/**
 * Fonte di verita` dei campi della ricerca mutuo per i tool WebMCP.
 *
 * Mappa nomi "amichevoli" (usati nello schema esposto all'agent) verso i campi
 * del postData del FormRicercaMutuo, con i relativi vincoli. Gli stessi vincoli
 * vivono oggi in inputsRuleQueryString dentro FormRicercaMutuo (canale
 * query-string): la deduplica e` un follow-up, qui partiamo da una definizione
 * pulita e indipendente per non toccare quel flusso.
 *
 * I campi a scelta multipla (finalita, tipoTasso, classeEnergetica) NON hanno
 * choices scritte a mano: le derivano dalle STESSE fonti usate dal ricercatore
 * del sito, cosi` un aggiornamento la` si riflette qui senza doppioni:
 *   - finalita        -> labels.finalita   (src/utils/labels/finalita.json)
 *   - tipoTasso       -> labels.tipoTasso  (src/utils/labels/tipo_tasso.json)
 *   - classeEnergetica-> classeEnergeticaOptions (Inputs/ClasseEnergetica.jsx)
 * La description di ciascuno include la legenda completa CHIAVE = descrizione,
 * perche' i codici da soli non sono parlanti e rischiano di essere fraintesi.
 */

import { labels } from "../utils/labels";
import { classeEnergeticaOptions } from "../components/FormRicercaMutuo/Inputs/ClasseEnergetica";

// "Tutti" e` un'etichetta-filtro generica (usata altrove nella UI): non e` un
// tipo tasso ricercabile e non compare mai nella list del form, quindi lo
// escludiamo dalle choices esposte all'agent.
const TIPO_TASSO_EXCLUDED = new Set(["Tutti"]);

// Coppie [chiave, descrizione] per ciascun campo a scelta multipla, dall'unica
// fonte condivisa. Da queste derivano sia le choices sia la legenda.
const finalitaEntries = Object.entries(labels.finalita);
const tipoTassoEntries = Object.entries(labels.tipoTasso).filter(
  ([key]) => !TIPO_TASSO_EXCLUDED.has(key)
);
const classeEnergeticaEntries = classeEnergeticaOptions.map((o) => [
  o.value,
  o.label,
]);

const choicesFromEntries = (entries) => entries.map(([key]) => key);

/** "<intro> Valori ammessi: K1 = descr1; K2 = descr2; ...". */
const describeChoices = (intro, entries) => {
  const legenda = entries
    .map(([key, desc]) => `${key === "" ? "(vuoto)" : key} = ${desc}`)
    .join("; ");
  return `${intro} Valori ammessi: ${legenda}.`;
};

export const ricercaFieldRules = {
  finalita: {
    field: "Finalita",
    qsKey: "ddlFinalitaMutuo",
    type: "choices",
    choices: choicesFromEntries(finalitaEntries),
    description: describeChoices("Finalita` del mutuo.", finalitaEntries),
  },
  importoMutuo: {
    field: "ImportoMutuo",
    qsKey: "txtImportoRichiesto",
    type: "number",
    min: 50000,
    max: 50000000,
    description: "Importo del mutuo richiesto in euro.",
  },
  valoreImmobile: {
    field: "ValoreImmobile",
    qsKey: "txtValoreImmobile",
    type: "number",
    min: 50000,
    max: 50000000,
    description: "Valore dell'immobile in euro.",
  },
  durata: {
    field: "Durata",
    qsKey: "ddlDurata",
    type: "number",
    min: 5,
    max: 40,
    description: "Durata del mutuo in anni.",
  },
  tipoTasso: {
    field: "TipoTasso",
    qsKey: "ddlTipoTasso",
    type: "choices",
    choices: choicesFromEntries(tipoTassoEntries),
    description: describeChoices("Tipo di tasso del mutuo.", tipoTassoEntries),
  },
  provinciaImmobile: {
    field: "ProvinciaImmobile",
    qsKey: "ddlProvinciaImmobile",
    type: "string",
    minLength: 2,
    maxLength: 2,
    description: "Sigla provincia dell'immobile (2 lettere, es. MI).",
  },
  etaRichiedente: {
    field: "EtaRichiedente",
    qsKey: "txtEta",
    type: "number",
    min: 18,
    max: 70,
    description: "Eta` del richiedente.",
  },
  redditoMensile: {
    field: "RedditoMensile",
    qsKey: "txtReddito",
    type: "number",
    min: 0,
    max: 1000000,
    description: "Reddito mensile netto in euro.",
  },
  classeEnergetica: {
    field: "ClasseEnergetica",
    qsKey: "ddlClasseEnergetica",
    type: "choices",
    choices: choicesFromEntries(classeEnergeticaEntries),
    description: describeChoices(
      "Classe energetica dell'immobile.",
      classeEnergeticaEntries
    ),
  },
};

// Mappa campo-postData -> chiave query-string (per il fallback via navigazione).
const fieldToQsKey = Object.values(ricercaFieldRules).reduce((acc, r) => {
  acc[r.field] = r.qsKey;
  return acc;
}, {});

/**
 * Costruisce la query-string compatibile con FormRicercaMutuo a partire dai
 * parametri amichevoli. Ritorna { qs, rejected }.
 */
export function buildSearchQueryString(input = {}) {
  const { params, rejected } = sanitizeRicercaParams(input);
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([field, value]) => {
    const qsKey = fieldToQsKey[field];
    if (qsKey && value !== "" && value !== undefined && value !== null) {
      sp.set(qsKey, value);
    }
  });
  return { qs: sp.toString(), rejected };
}

/** Campi del tool "rapido". */
export const QUICK_FIELDS = [
  "finalita",
  "importoMutuo",
  "valoreImmobile",
  "durata",
  "tipoTasso",
];

/** Campi del tool "avanzato" (rapido + extra). */
export const ADVANCED_FIELDS = [
  ...QUICK_FIELDS,
  "provinciaImmobile",
  "etaRichiedente",
  "redditoMensile",
  "classeEnergetica",
];

/** Costruisce un JSON schema (tutti i campi opzionali) per i nomi indicati. */
export function buildInputSchema(fieldNames) {
  const properties = {};
  fieldNames.forEach((name) => {
    const rule = ricercaFieldRules[name];
    if (!rule) return;
    if (rule.type === "number") {
      properties[name] = {
        type: "number",
        minimum: rule.min,
        maximum: rule.max,
        description: rule.description,
      };
    } else if (rule.type === "choices") {
      properties[name] = {
        type: "string",
        enum: rule.choices,
        description: rule.description,
      };
    } else {
      properties[name] = {
        type: "string",
        minLength: rule.minLength,
        maxLength: rule.maxLength,
        description: rule.description,
      };
    }
  });
  return { type: "object", properties };
}

function isValid(rule, value) {
  if (value === undefined || value === null) return false;
  if (rule.type === "number") {
    const n = Number(value);
    if (!Number.isFinite(n)) return false;
    return n >= rule.min && n <= rule.max;
  }
  if (rule.type === "choices") {
    return rule.choices.includes(value);
  }
  if (rule.type === "string") {
    const s = String(value);
    return s.length >= rule.minLength && s.length <= rule.maxLength;
  }
  return false;
}

/**
 * Sanitizza i parametri amichevoli verso i campi del postData.
 * Ritorna { params, rejected }: params contiene solo i campi validi
 * (con i nomi del postData), rejected elenca quelli scartati.
 * ProvinciaImmobile viene specchiata anche su ProvinciaDomicilio.
 */
export function sanitizeRicercaParams(input = {}) {
  const params = {};
  const rejected = [];

  Object.entries(input).forEach(([key, value]) => {
    const rule = ricercaFieldRules[key];
    if (!rule) {
      rejected.push({ key, reason: "campo sconosciuto" });
      return;
    }
    const normalized = rule.type === "number" ? Number(value) : value;
    if (!isValid(rule, normalized)) {
      rejected.push({ key, reason: "valore non valido" });
      return;
    }
    params[rule.field] = normalized;
    if (rule.field === "ProvinciaImmobile") {
      params.ProvinciaDomicilio = normalized;
    }
  });

  return { params, rejected };
}
